const AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';

const DEVICE_KEY_SUFFIXES = ['deviceKey', 'deviceGroupKey', 'randomPasswordKey'];

const LAST_AUTH_USER_SUFFIX = 'LastAuthUser';

const SECRET_SUFFIX = 'randomPasswordKey';

const MIRROR_KEY = 'lusa.trustedDevice';

const EXPIRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const buildActiveKey = (clientId, lastAuthUser, suffix) => (
  `${AUTH_KEY_PREFIX}.${clientId}.${lastAuthUser}.${suffix}`
);

const buildLastAuthUserKey = (clientId) => (
  `${AUTH_KEY_PREFIX}.${clientId}.${LAST_AUTH_USER_SUFFIX}`
);

const scanDeviceKeys = (active) => {
  const suffixPattern = DEVICE_KEY_SUFFIXES.join('|');
  const deviceKeyMatcher = new RegExp(`^${AUTH_KEY_PREFIX}\\.(.+)\\.(.+)\\.(${suffixPattern})$`);

  const pairs = new Map();

  for (let i = 0; i < active.length; i += 1) {
    const storageKey = active.key(i);
    const match = storageKey && deviceKeyMatcher.exec(storageKey);
    if (match) {
      const [, clientId, lastAuthUser, suffix] = match;
      const pairKey = `${clientId}.${lastAuthUser}`;
      if (!pairs.has(pairKey)) {
        pairs.set(pairKey, { clientId, lastAuthUser, entries: {} });
      }
      pairs.get(pairKey).entries[suffix] = active.getItem(storageKey);
    }
  }

  if (pairs.size === 0) {
    return null;
  }

  const pairList = Array.from(pairs.values());
  const owningDeviceKey = pairList.find((pair) => 'deviceKey' in pair.entries);
  if (owningDeviceKey) {
    return owningDeviceKey;
  }

  const lastAuthUserMatcher = new RegExp(`^${AUTH_KEY_PREFIX}\\.(.+)\\.${LAST_AUTH_USER_SUFFIX}$`);
  for (let i = 0; i < active.length; i += 1) {
    const storageKey = active.key(i);
    const match = storageKey && lastAuthUserMatcher.exec(storageKey);
    if (match) {
      const [, clientId] = match;
      const lastAuthUser = active.getItem(storageKey);
      const pairKey = `${clientId}.${lastAuthUser}`;
      if (pairs.has(pairKey)) {
        return pairs.get(pairKey);
      }
    }
  }

  return pairList[0];
};

const readRecord = (mirror) => {
  const raw = mirror.getItem(MIRROR_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (err) {
    return null;
  }
};

const writeRecord = (mirror, record) => {
  mirror.setItem(MIRROR_KEY, JSON.stringify(record));
};

const removeRecord = (mirror) => {
  mirror.removeItem(MIRROR_KEY);
};

const isExpired = (record, now) => now - record.trustTimestamp > EXPIRY_WINDOW_MS;

const warn = (message) => {
  console.warn(`[deviceKeyStorage] ${message}`);
};

const persistTrustedDevice = (deps = {}) => {
  const {
    active = window.sessionStorage,
    mirror = window.localStorage,
    now = Date.now,
  } = deps;

  try {
    const scanned = scanDeviceKeys(active);
    if (!scanned) {
      return false;
    }

    const { clientId, lastAuthUser, entries } = scanned;

    const record = {
      clientId,
      lastAuthUser,
      ...entries,
      trustTimestamp: now(),
    };

    writeRecord(mirror, record);
    return true;
  } catch (err) {
    warn('persistTrustedDevice failed; device trust not mirrored');
    return false;
  }
};

const restoreTrustedDevice = (deps = {}) => {
  const {
    active = window.sessionStorage,
    mirror = window.localStorage,
    now = Date.now,
  } = deps;

  try {
    const record = readRecord(mirror);
    if (!record) {
      return 'none';
    }

    const { clientId, lastAuthUser } = record;

    if (isExpired(record, now())) {
      removeRecord(mirror);
      DEVICE_KEY_SUFFIXES.forEach((suffix) => {
        if (suffix in record) {
          active.removeItem(buildActiveKey(clientId, lastAuthUser, suffix));
        }
      });
      const purgeLastAuthUserKey = buildLastAuthUserKey(clientId);
      if (active.getItem(purgeLastAuthUserKey) === lastAuthUser) {
        active.removeItem(purgeLastAuthUserKey);
      }
      return 'purged';
    }

    DEVICE_KEY_SUFFIXES.forEach((suffix) => {
      if (suffix in record) {
        active.setItem(buildActiveKey(clientId, lastAuthUser, suffix), record[suffix]);
      }
    });
    const lastAuthUserKey = buildLastAuthUserKey(clientId);
    if (!active.getItem(lastAuthUserKey)) {
      active.setItem(lastAuthUserKey, lastAuthUser);
    }
    return 'restored';
  } catch (err) {
    warn('restoreTrustedDevice failed; device trust not restored');
    return 'none';
  }
};

const restoreTrustedDeviceAsync = async (deps = {}) => {
  const {
    active,
    mirror = window.localStorage,
    now = Date.now,
  } = deps;

  try {
    if (!active || typeof active.setItem !== 'function') {
      return 'none';
    }

    const record = readRecord(mirror);
    if (!record) {
      return 'none';
    }

    const { clientId, lastAuthUser } = record;

    if (isExpired(record, now())) {
      removeRecord(mirror);
      await Promise.all(
        DEVICE_KEY_SUFFIXES
          .filter((suffix) => suffix in record)
          .map((suffix) => active.removeItem(buildActiveKey(clientId, lastAuthUser, suffix)))
      );

      const purgeLastAuthUserKey = buildLastAuthUserKey(clientId);
      const currentPointer = await active.getItem(purgeLastAuthUserKey);
      if (currentPointer === lastAuthUser) {
        await active.removeItem(purgeLastAuthUserKey);
      }
      return 'purged';
    }

    await Promise.all(
      DEVICE_KEY_SUFFIXES
        .filter((suffix) => suffix in record)
        .map((suffix) => active.setItem(buildActiveKey(clientId, lastAuthUser, suffix), record[suffix]))
    );
    const lastAuthUserKey = buildLastAuthUserKey(clientId);
    const existingPointer = await active.getItem(lastAuthUserKey);
    if (!existingPointer) {
      await active.setItem(lastAuthUserKey, lastAuthUser);
    }
    return 'restored';
  } catch (err) {
    warn('restoreTrustedDeviceAsync failed; device trust not restored');
    return 'none';
  }
};

export {
  AUTH_KEY_PREFIX,
  DEVICE_KEY_SUFFIXES,
  LAST_AUTH_USER_SUFFIX,
  SECRET_SUFFIX,
  MIRROR_KEY,
  EXPIRY_WINDOW_MS,
  buildActiveKey,
  readRecord,
  writeRecord,
  removeRecord,
  isExpired,
  warn,
  persistTrustedDevice,
  restoreTrustedDevice,
  restoreTrustedDeviceAsync,
};
